import { hexToWorld } from '../hex.ts';
import { cosA, dist2, idiv, imax, imin, isqrt, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import { enemiesNear, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { beamTrace } from '../battle/litter-layout.ts';
import { litterOrder, LITTER_BACK, MIRROR_MARK } from '../battle/litter.ts';
import { LITTER_GO, POST_MARK, plankPoint, walkPlankAt } from '../battle/pasarela.ts';
import { plankAt, WALK_DOWN, WALK_ON, walkLit } from '../battle/pasarela-layout.ts';
import { frostSets, frostSolve, frostThawed, frostTrace, type FrostBoard } from '../battle/nieve-layout.ts';
import { nevBrazierAt, NEV_MARK, NEV_MIRROR, NEV_PILE } from '../battle/nieve.ts';
import { ermitaBot } from './senda-bot-ermita.ts';
import { vigiliaBot } from './senda-bot-vigilia.ts';

/**
 * The cleric's chapter 1 played by the lab's bot (docs/sendas/clerigo.md; ai/senda-bot.ts hands it the fight): one that
 * knows the act's rule and one that doesn't. Both heal (they're clerics): a mend on itself beside Tessa when she or it
 * is hurt, the sanctuary on the stretcher, dawn when it is low; and both get a fallen carrier up.
 *
 * - The lower street: who knows keeps at the stretcher's back and fights what comes at it (the stalkers show only in
 *   its light: there is nothing to hunt out in the dark); who doesn't goes after whatever it sees nearest (the crows off
 *   the roofs), and leaves the stretcher behind.
 * - The chapel: who knows reads the beam: hangs the lantern on the hook that looks nearer, follows the beam to the first
 *   mirror it hits and turns it until the beam goes on somewhere (or gives up on it after three turns and tries the
 *   other hook), and takes the lantern back to the stretcher to fight when Tessa is low; who doesn't taps hooks and
 *   mirrors as they come and never goes back for the stretcher.
 * - The slope (the test): who knows peels the limpets off the stretcher first (a holy blow), then kills those on itself;
 *   who doesn't heals Tessa and fights the crows and what crawls, but never strikes a limpet stuck on the stretcher.
 *
 * It only moves the hero, picks its target and casts, as a player's commands would.
 */

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (h: Hero, t: Enemy): void => {
  h.target = t.id;
  h.forced = true;
};

const person = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/** The cleric's heals: a mend on itself beside Tessa, the sanctuary over the stretcher, dawn when it is low. */
const heal = (s: BattleState, h: Hero): void => {
  const r = s.rules?.senda?.litter;
  const t = person(s, 'tessa');
  if (!r || !t) return;
  const beside = withinRange(h.x, h.y, t.x, t.y, r.mendReach - 300);
  const tessaLow = t.hp > 0 && t.hp <= t.maxHp - 4;
  const meLow = h.hp * 10 < h.stats.maxHp * 6;
  if (h.skillCd[0] === 0 && ((beside && tessaLow) || meLow)) castSkill(s, h, { t: 'skill', slot: 0, id: h.id });
  if (h.skillCd[1] === 0 && beside && (t.hp * 2 <= t.maxHp || h.hp * 2 < h.stats.maxHp)) castSkill(s, h, { t: 'skill', slot: 1, x: t.x, y: t.y });
  if (h.skillCd[2] === 0 && h.hp * 10 < h.stats.maxHp * 3) castSkill(s, h, { t: 'skill', slot: 2 });
};

/** A carrier down: to them, and stand by them (true while it does). */
const lift = (s: BattleState, h: Hero): boolean => {
  const r = s.rules?.senda?.litter;
  if (!r) return false;
  for (const id of ['piet', 'ludo']) {
    const c = person(s, id);
    if (!c || c.hp > 0) continue;
    // Standing by them; striking only what is in reach from there (a chase would walk it off).
    const by = withinRange(h.x, h.y, c.x, c.y, r.liftReach - 300);
    const t = by ? nearestTo(enemiesNear(s, h.x, h.y, h.stats.range - 300, (e) => targetable(s, e)), h.x, h.y) : null;
    if (t) strike(h, t);
    else {
      h.target = 0;
      h.forced = false;
    }
    if (!withinRange(h.x, h.y, c.x, c.y, r.liftReach - 400)) go(s, h, c.x, c.y, 200);
    return true;
  }
  return false;
};

/** Behind the stretcher along its way (it goes on with the hero within reach). */
const followLitter = (s: BattleState, h: Hero, back: number): void => {
  const w = s.senda?.litter;
  if (!w) return;
  const fx = idiv(cosA(w.facing) * back, 10000);
  const fy = idiv(sinA(w.facing) * back, 10000);
  const x = w.x - fx;
  const y = w.y - fy;
  if (!withinRange(h.x, h.y, x, y, 900)) go(s, h, x, y, 500);
};

/* ---------- the lower street ---------- */

const streetBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const w = s.senda?.litter;
  if (!w) return;
  heal(s, h);
  if (lift(s, h)) return;
  if (knows) {
    const near = enemiesNear(s, w.x, w.y, 3600, (e) => targetable(s, e));
    const t = nearestTo(near, w.x, w.y) ?? nearestTo(enemiesNear(s, h.x, h.y, 2200, (e) => targetable(s, e)), h.x, h.y);
    if (t) strike(h, t);
    else h.target = 0;
    followLitter(s, h, 1300);
    return;
  }
  // Who doesn't know walks ahead clearing the way (out of the light), after whatever it sees.
  const t = nearestTo(enemiesNear(s, h.x, h.y, 9000, (e) => targetable(s, e)), h.x, h.y);
  if (t) {
    strike(h, t);
    if (!withinRange(h.x, h.y, t.x, t.y, h.stats.range)) go(s, h, t.x, t.y, 600);
    return;
  }
  h.target = 0;
  followLitter(s, h, -3500);
};

/* ---------- the chapel ---------- */

/** What the bot that knows is doing in the chapel: the hook it tried, the mirror it is turning and how many times. */
const CHAPEL = new WeakMap<BattleState, { hook: number; mirror: number; turns: number; wait: number; busy: boolean }>();
/** How long a person looks at the beam before the next move (ticks). */
const THINK = 48;

const chapelBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const M = r?.mirrors;
  const t = person(s, 'tessa');
  if (!r || !w || !M || !t) return;
  heal(s, h);
  if (w.lit >= 0) return;
  // Fighting by the stretcher first, while anything is on it in the light.
  const onIt = enemiesNear(s, w.x, w.y, 2600, (e) => targetable(s, e));
  if (knows && w.lantern < 0 && onIt.length) {
    // (Another order of the player's: what it was sent to do is off.)
    w.job = -1;
    strike(h, nearestTo(onIt, w.x, w.y) as Enemy);
    if (!withinRange(h.x, h.y, w.x, w.y, 2400)) go(s, h, w.x + 700, w.y - 300);
    return;
  }
  h.target = 0;
  // A person reads the beam before the next move: a pause after each one is done (`THINK`).
  let p0 = CHAPEL.get(s);
  if (!p0) {
    p0 = { hook: -1, mirror: -1, turns: 0, wait: s.tick + THINK, busy: false };
    CHAPEL.set(s, p0);
  }
  if (w.job >= 0) {
    p0.busy = true;
    return;
  }
  if (p0.busy) {
    p0.busy = false;
    p0.wait = s.tick + THINK;
  }
  if (s.tick < p0.wait) return;
  if (!knows) {
    // Tapping what comes: a hook, a mirror, another.
    const k = (s.tick >> 3) % 4;
    litterOrder(s, h, k < 2 ? k : MIRROR_MARK + (k - 2));
    return;
  }
  // Tessa low in the dark: back for the lantern; with it back, by her until she is up again.
  if (w.lantern >= 0 && t.hp * 2 <= t.maxHp) {
    litterOrder(s, h, LITTER_BACK);
    return;
  }
  if (w.lantern < 0 && t.hp * 10 < t.maxHp * 7 && p0.hook >= 0) {
    if (!withinRange(h.x, h.y, w.x, w.y, 1600)) go(s, h, w.x + 700, w.y - 300);
    return;
  }
  const p = p0;
  if (p.hook < 0) {
    // First, the hook that looks nearer to the lamp (the one a player tries first).
    const L = hexToWorld(M.lamp.q, M.lamp.r);
    const order = M.hooks.map((x, i) => ({ i, d: dist2(hexToWorld(x.q, x.r).x, hexToWorld(x.q, x.r).y, L.x, L.y) })).sort((a, b) => a.d - b.d);
    p.hook = order[0]?.i ?? 0;
  }
  if (w.lantern !== p.hook) {
    litterOrder(s, h, p.hook);
    return;
  }
  // Read the beam: follow it mirror by mirror; the first one whose beam goes on to nothing new is where it goes wrong.
  const b = beamTrace({ hooks: M.hooks, mirrors: M.mirrors, lamp: M.lamp, walls: M.walls, radius: M.radius }, w.lantern, w.turns);
  let at = -1;
  for (let i = 0; i < b.by.length; i++) {
    const next = b.by[i + 1];
    if (next === undefined || b.by.indexOf(next) <= i) {
      at = b.by[i] as number;
      break;
    }
  }
  if (at < 0) {
    p.hook = 1 - p.hook;
    p.mirror = -1;
    p.turns = 0;
    return;
  }
  if (p.mirror !== at) {
    p.mirror = at;
    p.turns = 0;
  }
  if (p.turns >= 3) {
    // Round all three and nothing: the other hook.
    p.hook = 1 - p.hook;
    p.mirror = -1;
    p.turns = 0;
    return;
  }
  p.turns++;
  litterOrder(s, h, MIRROR_MARK + at);
};

/* ---------- the slope of the limpets ---------- */

const PACE = new WeakMap<BattleState, number>();

const slopeBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const w = s.senda?.litter;
  if (!w) return;
  heal(s, h);
  if (lift(s, h)) return;
  const L = w.limpets;
  const of = (on: number): Enemy[] => L.filter((l) => l.on === on).map((l) => s.enemies.find((e) => e.id === l.id)).filter((e): e is Enemy => !!e && e.hp > 0);
  const stuck = of(1);
  const mine = of(2);
  const limpet = new Set(L.map((l) => l.id));
  const others = enemiesNear(s, w.x, w.y, 5000, (e) => targetable(s, e) && !limpet.has(e.id));
  const crawling = of(0);
  const pecking = others.filter((e) => e.kind === 'crow' && withinRange(e.x, e.y, w.x, w.y, 1600));
  const t = knows ? (stuck[0] ?? nearestTo(pecking, w.x, w.y) ?? mine[0] ?? nearestTo(others, w.x, w.y) ?? nearestTo(crawling, w.x, w.y)) : (mine[0] ?? nearestTo(others, w.x, w.y) ?? nearestTo(crawling, w.x, w.y));
  if (t) {
    strike(h, t);
    followLitter(s, h, 1200);
    return;
  }
  h.target = 0;
  if (!knows && stuck.length) {
    // (Not striking them: pacing beside the stretcher, healing.)
    // (A hero standing still strikes what is in reach on its own: it keeps walking.)
    let k = PACE.get(s) ?? 0;
    if (!h.moving) {
      k++;
      PACE.set(s, k);
      orderMove(s, h, w.x + (k % 2 ? 1500 : -1500), w.y + 700);
    }
    return;
  }
  followLitter(s, h, 1200);
};

/* ---------- chapter 2: the cliff path ---------- */

/** Ticks the stretcher needs from where it is to the way's point `to`. */
const ticksTo = (s: BattleState, to: number): number => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  if (!r || !w) return 0;
  let d = 0;
  let x = w.x;
  let y = w.y;
  for (let i = w.seg + 1; i <= to && i < r.way.length; i++) {
    const p = r.way[i] as { x: number; y: number };
    d += isqrt(dist2(p.x, p.y, x, y));
    x = p.x;
    y = p.y;
  }
  return idiv(d, imax(1, r.pace));
};

const ridgeBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const p = w?.pas;
  const R = r?.ridge;
  if (!r || !w || !p || !R) return;
  heal(s, h);
  if (lift(s, h)) return;
  // Waiting in a chapel: who knows goes when the next volley lands after the next chapel (a breath of margin); who
  // doesn't, at once.
  if (p.wait >= 0) {
    const next = R.chapels[p.wait + 1];
    const need = next ? ticksTo(s, next.at) : ticksTo(s, r.way.length - 1);
    const gap = p.volley - s.waveT;
    if (!knows || (p.marks.length === 0 && gap >= need + 10) || (!next && gap >= need)) litterOrder(s, h, LITTER_GO);
  }
  // The volley coming: who knows steps out of its mark (toward the stretcher's chapel, if it is in one) and, caught in
  // the open with the stretcher, lays the sanctuary on it.
  const mx = p.marks[0];
  const my = p.marks[1];
  if (knows && mx !== undefined && my !== undefined) {
    const t = person(s, 'tessa');
    const open = !R.chapels.some((c) => withinRange(w.x, w.y, c.x, c.y, R.shade));
    if (open && t && h.skillCd[1] === 0 && withinRange(h.x, h.y, w.x, w.y, 3000)) castSkill(s, h, { t: 'skill', slot: 1, x: w.x, y: w.y });
    const c = R.chapels.find((k) => withinRange(w.x, w.y, k.x, k.y, R.shade));
    if (withinRange(h.x, h.y, mx, my, R.volley.r + 200)) {
      if (c) go(s, h, c.x + 200, c.y, 200);
      else go(s, h, mx + (h.x > w.x ? 1300 : -1300), my + 400, 300);
      return;
    }
  }
  // Fighting what goes for the carriers (the slingers are out of reach: never them).
  const foes = enemiesNear(s, w.x, w.y, 4200, (e) => targetable(s, e) && e.kind !== 'slinger');
  const t = nearestTo(foes, w.x, w.y);
  if (t) strike(h, t);
  else h.target = 0;
  if (knows) followLitter(s, h, 1200);
  else if (t && !withinRange(h.x, h.y, t.x, t.y, h.stats.range)) go(s, h, t.x, t.y, 600);
  else followLitter(s, h, 900);
};

/* ---------- chapter 2: the walkway ---------- */

const WALK = new WeakMap<BattleState, { wait: number; busy: boolean }>();

/** The span the stretcher needs lit next: the one it is on, or the one ahead of the landing it stands at. */
const needSpan = (s: BattleState): number => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const W = r?.walk;
  if (!r || !w || !W) return 0;
  const sp = W.waySpan[w.seg] ?? -1;
  if (sp >= 0) return sp;
  return imin(W.spans.length - 1, idiv(w.seg, 5));
};

/** Whether the hero can walk from where it is to landing `to` without stepping on ink or a gap. */
const walkable = (s: BattleState, h: Hero, to: number): boolean => {
  const r = s.rules?.senda?.litter;
  const p = s.senda?.litter?.pas;
  const W = r?.walk;
  if (!r || !p || !W) return false;
  const here = walkPlankAt(r, h.x, h.y);
  const from = here >= 0 ? idiv(here, 4) : landingOf(s, h);
  const lo = imin(from, to);
  const hi = imax(from, to);
  for (let i = lo; i < hi; i++) {
    if ((p.clean[i] ?? 0) < W.burn) return false;
    // (A span about to go dark again: not without time to cross it.)
    if (p.lit !== i && W.cover - (p.dark[i] ?? 0) < 48) return false;
    for (let k = 0; k < 4; k++) if (p.torn[i * 4 + k]) return false;
  }
  return true;
};

/** The landing nearest the hero (0 the foot). */
const landingOf = (s: BattleState, h: Hero): number => {
  const W = s.rules?.senda?.litter?.walk;
  if (!W) return 0;
  let best = 0;
  let bd = 0x7fffffff;
  W.landings.forEach((l, i) => {
    const c = hexToWorld(l.q, l.r);
    const d = dist2(h.x, h.y, c.x, c.y);
    if (d < bd) {
      bd = d;
      best = i;
    }
  });
  return best;
};

const walkBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const p = w?.pas;
  const W = r?.walk;
  if (!r || !w || !p || !W) return;
  heal(s, h);
  if (lift(s, h)) return;
  // Sent to a post: on its way and turning it, nothing else.
  if (w.job >= 0) {
    h.target = 0;
    h.forced = false;
    const m0 = WALK.get(s);
    if (m0) m0.busy = true;
    return;
  }
  const foes = enemiesNear(s, h.x, h.y, h.stats.range, (e) => targetable(s, e));
  const tg = nearestTo(foes, h.x, h.y);
  if (tg) strike(h, tg);
  else h.target = 0;
  // Piet hanging: to him.
  const piet = person(s, 'piet');
  if (p.slip >= 0 && piet) {
    w.job = -1;
    if (!withinRange(h.x, h.y, piet.x, piet.y, r.liftReach - 300)) go(s, h, piet.x - 300, piet.y + 200, 200);
    return;
  }
  // A torn plank ahead of the stretcher: by it, still, to lay it.
  const ahead = walkPlankAt(r, w.x, w.y);
  for (let k = imax(0, ahead); k < imin(p.torn.length, imax(0, ahead) + 4); k++) {
    if (!p.torn[k]) continue;
    const q = plankPoint(r, k - 1 >= 0 && !p.torn[k - 1] ? k - 1 : k);
    const sp = idiv(k, 4);
    if (q && (p.clean[sp] ?? 0) >= W.burn) {
      w.job = -1;
      if (!withinRange(h.x, h.y, q.x, q.y, 300)) go(s, h, q.x, q.y, 200);
      return;
    }
  }
  let m = WALK.get(s);
  if (!m) {
    m = { wait: s.tick + THINK, busy: false };
    WALK.set(s, m);
  }
  if (w.job >= 0) {
    m.busy = true;
    return;
  }
  if (m.busy) {
    m.busy = false;
    m.wait = s.tick + THINK;
  }
  const T = needSpan(s);
  if (s.tick >= m.wait) {
    if (!knows) {
      // Turning the post it stands by (or the nearest), and on: never minding the ink between.
      const at = landingOf(s, h);
      if (at < W.posts.length && walkLit(w.turns) !== T) {
        litterOrder(s, h, POST_MARK + (s.tick % 7 < 3 ? at : imin(W.posts.length - 1, at + 1)));
        return;
      }
    } else if (landingOf(s, h) < T && !walkable(s, h, T) && walkPlankAt(r, h.x, h.y) < 0) {
      // Cut off behind the stretcher (the span ahead of it under ink): its own post down, to light its way.
      const H = landingOf(s, h);
      if (w.turns[H] !== WALK_DOWN) {
        litterOrder(s, h, POST_MARK + H);
        return;
      }
      // (Its own post down and still dark: one before it isn't on; back to it, over the ink if it must.)
      for (let j = H - 1; j >= 0; j--)
        if (w.turns[j] !== WALK_ON) {
          litterOrder(s, h, POST_MARK + j);
          return;
        }
    } else if (walkLit(w.turns) !== T) {
      // The post furthest on that is wrong first (the next one down while the light is still here), then back.
      for (let j = T; j >= 0; j--) {
        const want = j === T ? WALK_DOWN : WALK_ON;
        if (w.turns[j] === want) continue;
        if (walkable(s, h, j)) litterOrder(s, h, POST_MARK + j);
        return;
      }
    }
  }
  // By the stretcher, a little behind it, on clean ground.
  const at = W.waySpan[w.seg] ?? -1;
  if (at < 0) {
    const l = W.landings[idiv(w.seg, 5)] as { q: number; r: number };
    const c = hexToWorld(l.q, l.r);
    if (!withinRange(h.x, h.y, c.x - 300, c.y + 150, 500) && (knows ? walkable(s, h, idiv(w.seg, 5)) : true)) go(s, h, c.x - 300, c.y + 150, 200);
  } else followLitter(s, h, 1000);
};

/* ---------- chapter 2: the chapels' bridge (the test) ---------- */

const bridgeBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const p = w?.pas;
  const B = r?.span;
  if (!r || !w || !p || !B) return;
  heal(s, h);
  if (lift(s, h)) return;
  const M = B.mangonel;
  // Go or wait: who knows waits for a stone landing where the stretcher would be, and goes out of one landing on it;
  // who doesn't always goes.
  let want = true;
  if (knows) {
    for (const st of p.stones) {
      const left = st.at - s.waveT;
      const moving = w.y - r.pace * left;
      const hitStill = withinRange(w.x, w.y, st.x, st.y, M.r);
      const hitGoing = withinRange(w.x, moving, st.x, st.y, M.r + 300);
      // (Its shadow on the way: wait for it.)
      if (hitGoing || hitStill) want = false;
    }
  }
  if (w.done < 0 && want !== p.go) litterOrder(s, h, LITTER_GO);
  // Out of a stone's shadow that is about to land.
  if (knows) {
    for (const st of p.stones) {
      if (st.at - s.waveT < 20 && withinRange(h.x, h.y, st.x, st.y, M.r + 200)) {
        go(s, h, st.x + (h.x >= st.x ? 1500 : -1500), h.y, 200);
        return;
      }
    }
  }
  // The plank under the stretcher first, then the ones just ahead: by it, still.
  const here = plankAt(w.y);
  const order = knows ? [here, here + 1, here + 2] : [here + 1];
  for (const k of order) {
    if (k < 0 || k >= p.torn.length || !p.torn[k]) continue;
    const q = plankPoint(r, k);
    if (!q) continue;
    // (On the plank before it, toward the foot: the torn one can't be stood on to lay it.)
    const by = { x: 0, y: q.y + 866 };
    if (!withinRange(h.x, h.y, q.x, q.y, B.mendReach - 250) && !withinRange(h.x, h.y, by.x, by.y, 350)) {
      // (Walking there: a target would plant it where it stands.)
      h.target = 0;
      h.forced = false;
      go(s, h, by.x, by.y, 200);
      return;
    }
    if (h.moving) orderMove(s, h, h.x, h.y);
    // (Still, striking what is in reach: a gargoyle on a plank first, then a limpet on the stretcher.)
    const inReach = (e: Enemy | undefined): e is Enemy => !!e && e.hp > 0 && withinRange(h.x, h.y, e.x, e.y, h.stats.range);
    const sat = p.perch.map((x) => s.enemies.find((e) => e.id === x.id)).find(inReach);
    const stuck = w.limpets.filter((l) => l.on === 1).map((l) => s.enemies.find((e) => e.id === l.id)).find(inReach);
    const near = (knows ? (sat ?? stuck) : undefined) ?? nearestTo(enemiesNear(s, h.x, h.y, h.stats.range, (e) => targetable(s, e) && e.id !== p.boss), h.x, h.y);
    if (near) strike(h, near);
    return;
  }
  // The limpets on the stretcher (who knows), the gargoyles on the planks by it, what is near.
  const L = w.limpets;
  const of = (on: number): Enemy[] => L.filter((l) => l.on === on).map((l) => s.enemies.find((e) => e.id === l.id)).filter((e): e is Enemy => !!e && e.hp > 0);
  const perched = p.perch.map((x) => s.enemies.find((e) => e.id === x.id)).filter((e): e is Enemy => !!e && e.hp > 0 && withinRange(e.x, e.y, w.x, w.y, 4000));
  const others = enemiesNear(s, w.x, w.y, 4500, (e) => targetable(s, e) && e.id !== p.boss);
  const t = knows ? (perched[0] ?? of(1)[0] ?? of(2)[0] ?? nearestTo(others, w.x, w.y)) : (of(2)[0] ?? nearestTo(others, w.x, w.y));
  // (Far behind it: walking first, striking on the way would plant it.)
  const far = !withinRange(h.x, h.y, w.x, w.y + 1100, 1800);
  if (t && !far) strike(h, t);
  else {
    h.target = 0;
    h.forced = false;
  }
  followLitter(s, h, 1100);
};

/* ---------- chapter 3: the white col ---------- */

const colBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const S = r?.snow;
  const n = w?.nev;
  if (!r || !w || !S || !n) return;
  heal(s, h);
  if (lift(s, h)) return;
  // Who knows lights the next chapel's brazier ahead as the stretcher comes up to it (running ahead a little).
  if (knows && w.done < 0) {
    if (w.job >= 0) {
      h.target = 0;
      h.forced = false;
      return;
    }
    for (let i = 0; i < S.braziers.length; i++) {
      const b = S.braziers[i] as { x: number; y: number };
      if (n.lit[i]) continue;
      const ahead = w.y - b.y;
      if (ahead < -2200 || ahead > 4600) continue;
      // (Wolves on the stretcher first, unless it is cold.)
      const onIt = enemiesNear(s, w.x, w.y, 1600, (e) => targetable(s, e));
      if (onIt.length && n.cold * 10 > S.cold.full * 8) break;
      litterOrder(s, h, NEV_MARK + i);
      return;
    }
  }
  const foes = enemiesNear(s, w.x, w.y, 4200, (e) => targetable(s, e));
  const t = nearestTo(foes, w.x, w.y);
  if (t) strike(h, t);
  else h.target = 0;
  if (knows) followLitter(s, h, 1100);
  else if (t && !withinRange(h.x, h.y, t.x, t.y, h.stats.range)) go(s, h, t.x, t.y, 600);
  else followLitter(s, h, 900);
};

/* ---------- chapter 3: the frozen mirrors ---------- */

const FROST = new WeakMap<BattleState, { wait: number; busy: boolean; plan: number[]; want: number[] | null; k: number }>();

const frostBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const F = r?.snow?.frost;
  const n = w?.nev;
  const t = person(s, 'tessa');
  if (!r || !w || !F || !n || !t) return;
  heal(s, h);
  if (w.lit >= 0) return;
  if (!n.carry && w.job < 0 && lift(s, h)) return;
  // Wolves at the stretcher: fought first.
  const onIt = enemiesNear(s, w.x, w.y, 2600, (e) => targetable(s, e));
  if (onIt.length && w.job < 0 && !n.carry) {
    strike(h, nearestTo(onIt, w.x, w.y) as Enemy);
    if (!withinRange(h.x, h.y, w.x, w.y, 2200)) go(s, h, w.x + 600, w.y - 300);
    return;
  }
  h.target = 0;
  let m = FROST.get(s);
  if (!m) {
    const B: FrostBoard = { lantern: F.lantern, door: F.door, mirrors: F.mirrors, walls: F.walls, radius: F.radius };
    // Who knows works the terrace out before the first brazier: the bases' circles, the faint ways in the frost.
    const warm = F.bases.findIndex((b) => {
      const c = hexToWorld(b.q, b.r);
      return withinRange(c.x, c.y, w.x, w.y, r.snow?.cold.warmR ?? 2300);
    });
    const sets = frostSets(B, F.thaws, imax(0, warm));
    const bits = sets.warm[0] ?? sets.lit[0] ?? 0;
    const plan: number[] = [];
    if (warm >= 0 && (bits >> warm) & 1) plan.push(warm);
    for (let i = 0; i < F.bases.length; i++) if ((bits >> i) & 1 && i !== warm) plan.push(i);
    const want = frostSolve(B, frostThawed(F.thaws, bits));
    m = { wait: s.tick + THINK, busy: false, plan, want, k: 0 };
    FROST.set(s, m);
  }
  if (w.job >= 0) {
    m.busy = true;
    return;
  }
  if (m.busy) {
    m.busy = false;
    m.wait = s.tick + THINK;
  }
  if (s.tick < m.wait) {
    if (!withinRange(h.x, h.y, w.x, w.y, 5200) && !n.carry) go(s, h, w.x + 700, w.y - 300);
    return;
  }
  if (!knows) {
    // Braziers on the bases nearest the pile, as they come; then tapping the mirrors that turn.
    const near = F.bases.map((b, i) => ({ i, d: dist2(hexToWorld(b.q, b.r).x, hexToWorld(b.q, b.r).y, F.pile.x, F.pile.y) })).sort((a, b) => a.d - b.d);
    const free = near.find((x) => !n.lit[x.i]);
    const placed = n.lit.reduce((a, v) => a + v, 0);
    if (placed < 3 && free) {
      litterOrder(s, h, n.carry ? NEV_MARK + free.i : NEV_PILE);
      return;
    }
    const thawed = F.mirrors.map((_, i) => i).filter((i) => !n.ice[i]);
    const k = thawed.length ? (thawed[(s.tick >> 4) % thawed.length] as number) : (s.tick >> 4) % F.mirrors.length;
    litterOrder(s, h, NEV_MIRROR + k);
    return;
  }
  // The braziers where the plan says, the stretcher's first.
  const next = m.plan.find((b) => !n.lit[b]);
  if (next !== undefined) {
    litterOrder(s, h, n.carry ? NEV_MARK + next : NEV_PILE);
    return;
  }
  // Then the mirrors the beam goes by, as the plan wants them (waiting for the frost to go).
  const want = m.want;
  if (!want) return;
  const b = frostTrace({ lantern: F.lantern, door: F.door, mirrors: F.mirrors, walls: F.walls, radius: F.radius }, w.turns, n.ice);
  for (const i of b.by)
    if (w.turns[i] !== want[i]) {
      litterOrder(s, h, NEV_MIRROR + i);
      return;
    }
  if (!withinRange(h.x, h.y, w.x, w.y, 2000)) go(s, h, w.x + 700, w.y - 300);
};

/* ---------- chapter 3: the night in the corral (the test) ---------- */

const corralBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const C = r?.snow?.corral;
  const n = w?.nev;
  if (!r || !w || !C || !n) return;
  heal(s, h);
  if (lift(s, h)) return;
  // The shadow on the floor: who knows stands in it for whoever it is meant for, and steps out of its own.
  if (knows && n.at >= 0) {
    const inIt = withinRange(h.x, h.y, n.ax, n.ay, C.watcher.r - 250);
    w.job = -1;
    if (n.who) {
      if (!inIt) {
        h.target = 0;
        h.forced = false;
        go(s, h, n.ax, n.ay, 150);
      } else if (h.moving) orderMove(s, h, h.x, h.y);
      return;
    }
    if (withinRange(h.x, h.y, n.ax, n.ay, C.watcher.r + 200)) {
      go(s, h, n.ax + (h.x >= n.ax ? 1600 : -1600), n.ay, 200);
      return;
    }
  }
  // One of the family frozen: to them (both know to get someone up).
  for (const f of C.family) {
    if (!n.frozen.includes(f.id)) continue;
    w.job = -1;
    if (!withinRange(h.x, h.y, f.x, f.y, r.liftReach - 300)) go(s, h, f.x + 300, f.y + 200, 200);
    else if (h.moving) orderMove(s, h, h.x, h.y);
    return;
  }
  const boss = s.enemies.find((e) => e.id === n.boss && e.hp > 0 && s.waveT < n.down);
  if (knows) {
    // The watcher down: at it.
    if (boss && withinRange(h.x, h.y, boss.x, boss.y, 3000)) {
      w.job = -1;
      strike(h, boss);
      return;
    }
    if (w.job >= 0) return;
    // A moth on a brazier: off it.
    const moth = n.snuff.map((x) => s.enemies.find((e) => e.id === x.id && e.hp > 0)).find((e): e is Enemy => !!e);
    if (moth) {
      strike(h, moth);
      return;
    }
    // A brazier out: lit again (the family's and Tessa's first: the nearest to them).
    let best = -1;
    let bd = 0x7fffffff;
    for (let i = 0; i < n.lit.length; i++) {
      if (n.lit[i]) continue;
      const p = nevBrazierAt(r, i);
      if (!p) continue;
      const fam = C.family[0] as { x: number; y: number };
      const d = imin(dist2(p.x, p.y, fam.x, fam.y), dist2(p.x, p.y, w.x, w.y));
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
    if (best >= 0) {
      litterOrder(s, h, NEV_MARK + best);
      return;
    }
  }
  // What is in the corral: wolves at the stretcher, moths.
  const foes = enemiesNear(s, w.x, w.y, 5200, (e) => targetable(s, e));
  const t = nearestTo(foes, h.x, h.y);
  if (t) {
    strike(h, t);
    if (!knows && !withinRange(h.x, h.y, t.x, t.y, h.stats.range)) go(s, h, t.x, t.y, 600);
    return;
  }
  h.target = 0;
  // Between the stretcher and the family.
  const fam = C.family[1] as { x: number; y: number };
  const mx = idiv(w.x + fam.x, 2);
  const my = idiv(w.y + fam.y, 2);
  if (!withinRange(h.x, h.y, mx, my, 900)) go(s, h, mx, my, 400);
};

/** The cleric's bot (true: it was one of its fights). */
export const clericBot = (s: BattleState, h: Hero, knows: boolean): boolean => {
  const r = s.rules?.senda?.litter;
  if (!r) return false;
  // (Chapter 4: ai/senda-bot-ermita.ts.)
  if (vigiliaBot(s, h, knows)) return true;
  if (ermitaBot(s, h, knows)) return true;
  if (r.kind === 'street') streetBot(s, h, knows);
  else if (r.kind === 'chapel') chapelBot(s, h, knows);
  else if (r.kind === 'ridge') ridgeBot(s, h, knows);
  else if (r.kind === 'walk') walkBot(s, h, knows);
  else if (r.kind === 'bridge') bridgeBot(s, h, knows);
  else if (r.kind === 'col') colBot(s, h, knows);
  else if (r.kind === 'frost') frostBot(s, h, knows);
  else if (r.kind === 'corral') corralBot(s, h, knows);
  else slopeBot(s, h, knows);
  return true;
};

